import { css } from 'aphrodite';
import moment from 'moment';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { DollarSignIcon } from '../../../../../../aida/components/svgs/icons/DollarSignIcon';
import * as Api from '../../../../../../extViewmodels';
import { useActionItemComposer, useErrorMessages, useNoteComposer } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getFileSizeStringValue,
} from '../../../../../../models/UiUtils';
import { Button } from '../../../../../components-v2/Button';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../../components-v2/Dropdown';
import { Pill } from '../../../../../components-v2/Pill';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { Attachments } from '../../../../../components/Attachments';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../components/ConfirmationDialog';
import { DefaultCompactEditorConfig } from '../../../../../components/notes/NoteEditor';
import { RichContentDocumentEditor } from '../../../../../components/richContent/RichContentDocumentEditor';
import { Separator } from '../../../../../components/Separator';
import { CustomSurveyIcon } from '../../../../../components/svgs/icons/CustomSurvey';
import { DonationSurveyIcon } from '../../../../../components/svgs/icons/DonationSurvey';
import { EventsSurveyIcon } from '../../../../../components/svgs/icons/EventsSurvey';
import { EntityTimelineSurveyResponseEventContent } from '../../../../../components/entities/EntityTimeline/presentation';
import { CalendarDateIcon } from '../../../../../components/svgs/icons/CalendarDateIcon';
import { CallIcon } from '../../../../../components/svgs/icons/CallIcon';
import { CheckmarkIcon } from '../../../../../components/svgs/icons/CheckmarkIcon';
import { ChevronDown } from '../../../../../components/svgs/icons/ChevronDown';
import { ClockIcon } from '../../../../../components/svgs/icons/ClockIcon';
import { EmailIcon } from '../../../../../components/svgs/icons/EmailIcon';
import { EyeIcon } from '../../../../../components/svgs/icons/EyeIcon';
import { NotesIcon } from '../../../../../components/svgs/icons/NotesIcon';
import { PostageIcon } from '../../../../../components/svgs/icons/PostageIcon';
import { QuestionMarkIcon } from '../../../../../components/svgs/icons/QuestionMarkIcon';
import { SatisfactionSurveyIcon } from '../../../../../components/svgs/icons/SatisfactionSurvey';
import { TextingIcon } from '../../../../../components/svgs/icons/TextingIcon';
import { UploadIconSimple } from '../../../../../components/svgs/icons/UploadIconSimple';
import { WarningIcon } from '../../../../../components/svgs/icons/WarningIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../components/Tooltip';
import { brandPrimary, emailActivityTintColor, header, noteTintColor, warningDark } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { TextingCampaignRegistrationContextProvider } from '../../../../Texting/TextingCampaignRegistrationPrompt/context';
import { ActionItemAction } from './events/ActionItem/Action';
import { ActionItemCheckboxIcon } from './events/ActionItem/CheckboxIcon';
import { ActionItemSubtitle } from './events/ActionItem/Subtitle';
import { EmailTitle } from './events/Email/Title';
import { HandWrittenCardExtraContent } from './events/HandWrittenCard/ExtraContent';
import { MeetingTitle } from './events/Meetings/Title';
import { ConversationThreadModal } from './events/Texting/ConversationThreadModal';
import { styleSheet } from './styles';
import { DownloadIconV2 } from '../../../../../components/svgs/icons/DownloadIconV2';

interface IProps {
	item: Api.TimelineEventViewModel<Api.ITimelineEvent>;
	contactEmailAddresses: Api.EmailAddress[];
	contactName: string;
	expandAllTimeline?: boolean;
	onDeleteRichContent: (richContent: Api.RichContentViewModel) => Promise<Api.IRichContent>;
	onMoreMenuOptionClicked?: (
		richContent: Api.RichContentViewModel,
		option: 'edit' | 'delete',
		e: React.MouseEvent<HTMLElement>
	) => void;
	onTimelineDataUpdated: () => void;
}

const decorateSurveyNameInTitle = (title: string | undefined, surveyName: string | undefined): ReactNode => {
	if (!title || !surveyName) {
		return title ?? '';
	}
	const idx = title.indexOf(surveyName);
	if (idx < 0) {
		return title;
	}
	return (
		<span>
			{title.substring(0, idx)}&ldquo;<strong>{surveyName}</strong>&rdquo;
		</span>
	);
};

export const ContactTimelineCard = ({
	item,
	contactEmailAddresses,
	contactName,
	expandAllTimeline,
	onDeleteRichContent,
	onTimelineDataUpdated,
}: IProps) => {
	const eventType = useMemo(() => {
		return item.type;
	}, [item]);

	const isExpandable = useMemo(() => {
		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
			case Api.TimelineEventType.HtmlNewsletterEvent:
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
			case Api.TimelineEventType.NoteEvent:
			case Api.TimelineEventType.SkipLeadEvent:
			case Api.TimelineEventType.ActionItemEvent:
			case Api.TimelineEventType.CustomSurveyResponseEvent:
			case Api.TimelineEventType.DonationSurveyResponseEvent:
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent:
				return true;
			case Api.TimelineEventType.PhoneCallEvent:
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent: {
				const phoneCallNote = item as Api.NoteEventViewModel;
				return !!phoneCallNote.viewmodel.rawContentState;
			}
			default:
				return false;
		}
	}, [eventType, item]);

	const shouldExpandByDefault = useMemo(() => {
		return isExpandable && (expandAllTimeline ?? false);
	}, [isExpandable, expandAllTimeline]);

	const [isOpen, setIsOpen] = useState(shouldExpandByDefault);
	const [isTextingModalOpen, setIsTextingModalOpen] = useState(false);
	const [showDeletingRichContentModal, setShowDeletingRichContentModal] = useState<Api.RichContentViewModel>(null);

	useEffect(() => {
		setIsOpen(shouldExpandByDefault);
	}, [shouldExpandByDefault]);

	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('ContactTimelineCard');
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();

	const onDeleteContentConfirmationDialogRequestClose = async (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!!result && !canceled && !!showDeletingRichContentModal) {
			logInput('MoreMenuDeleteNote', 'Click', {
				id: showDeletingRichContentModal.id,
			});
			try {
				await onDeleteRichContent(showDeletingRichContentModal);
				onTimelineDataUpdated();
			} catch (error) {
				logApiError('NoteDelete-Error', error);
				errorMessages.pushApiError(error);
			}
		}
		setShowDeletingRichContentModal(null);
	};

	const editClick = (richContent: Api.RichContentViewModel) => {
		if (richContent instanceof Api.NoteViewModel) {
			noteComposer.show(richContent, (editedNote?: Api.NoteViewModel, _result?: Api.RichContentComposerResult) => {
				if (!editedNote) {
					return;
				}

				onTimelineDataUpdated();
			});
		} else if (richContent instanceof Api.ActionItemViewModel) {
			actionItemComposer.show(
				richContent,
				(editedActionItem?: Api.ActionItemViewModel, _result?: Api.RichContentComposerResult) => {
					if (!editedActionItem) {
						return;
					}

					onTimelineDataUpdated();
				}
			);
		}
	};

	const renderMenu = (event: Api.TimelineEventViewModel) => {
		const richContent: Api.RichContentViewModel = (event as Api.NoteEventViewModel).viewmodel;
		if (!richContent) {
			return null;
		}
		return (
			<Dropdown>
				<DropdownEllipsisTrigger styles={[bs.ml2]} />
				<DropdownContent>
					{richContent.canEdit ? <DropdownItem onClick={() => editClick(richContent)}>Edit</DropdownItem> : null}
					{richContent.canDelete ? (
						<DropdownItem onClick={() => setShowDeletingRichContentModal(richContent)}>Delete</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
		);
	};

	const isActionItem = useMemo(() => {
		return eventType === 'ActionItemEvent';
	}, [eventType]);

	const isOverdue = useMemo(() => {
		if (!isActionItem) {
			return false;
		}

		const actionItem = item as Api.ActionItemEventViewModel;

		return actionItem.viewmodel.dueDate
			? moment(actionItem.viewmodel.dueDate).isBefore(moment()) && !actionItem.viewmodel.isCompleted
			: false;
	}, [item, isActionItem]);

	const getTitle = () => {
		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
			case Api.TimelineEventType.ExternalSentEmailEvent:
			case Api.TimelineEventType.HtmlNewsletterEvent:
				return (
					<EmailTitle
						event={item as Api.SentEmailEventViewModel}
						contactEmailAddresses={contactEmailAddresses}
						contactName={contactName}
					/>
				);
			case Api.TimelineEventType.MeetingEvent:
			case Api.TimelineEventType.MeetingScheduledEvent:
				return (
					<MeetingTitle
						event={item as Api.MeetingEventViewModel}
						contactName={contactName}
						contactEmailAddresses={contactEmailAddresses}
					/>
				);
			case Api.TimelineEventType.DealCreatedEvent:
			case Api.TimelineEventType.DealUpdatedEvent:
				return 'Deal created';
			case Api.TimelineEventType.NoteEvent: {
				const noteEvent = item as Api.NoteEventViewModel;
				const associations = noteEvent.viewmodel.externalAssociations;

				if (associations && associations.length > 0) {
					// DISPLAY LOGIC: Prioritize associations with timestamps (successful syncs with dates)
					// because this gives users the most valuable information about when data was last synced
					const associationToShow =
						associations.find((assoc: Api.IExternalAssociation) => assoc.timestamp) || associations[0];

					// FAILURE DETECTION: Check ALL associations for failures, not just the one we're displaying
					// This ensures users are alerted to ANY sync issues, even if we're showing a successful sync
					const hasFailure = associations.some((assoc: Api.IExternalAssociation) => assoc.failure);
					const failureMessage = associations.find((assoc: Api.IExternalAssociation) => assoc.failure)?.failure;

					const syncText = associationToShow.timestamp
						? ` - Synced with ${associationToShow.source} ${moment(associationToShow.timestamp).format('MM/DD/YY')}`
						: ` - Synced with ${associationToShow.source}`;

					return (
						<div className={css(bs.truncateText)}>
							{item.title}
							<span className={css(styleSheet.externalAssociationInline)}>
								{syncText}
								{hasFailure && (
									<Tooltip>
										<TooltipTrigger className={css(styleSheet.errorIcon)}>⚠️</TooltipTrigger>
										<TooltipContent>{failureMessage}</TooltipContent>
									</Tooltip>
								)}
							</span>
						</div>
					);
				}
				return item.title;
			}
			case Api.TimelineEventType.SkipLeadEvent:
				return item.title;
			case Api.TimelineEventType.ActionItemEvent:
				return (item as Api.ActionItemEventViewModel).title;
			case Api.TimelineEventType.ConversationThreadEvent:
				return (item as Api.ConversationThreadEventViewModel).toJs().title;
			case Api.TimelineEventType.PhoneCallEvent:
				return (item as Api.PhoneCallEventViewModel).toJs().title;
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent:
				return 'Phone call created';
			case Api.TimelineEventType.FollowUpEvent:
			case Api.TimelineEventType.CancelledFollowUpEvent:
			case Api.TimelineEventType.RescheduledFollowUpEvent:
				return 'Follow up created';
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
				return item.title;
			case Api.TimelineEventType.CustomSurveyResponseEvent:
			case Api.TimelineEventType.DonationSurveyResponseEvent:
				return decorateSurveyNameInTitle(item.title, (item as Api.SurveyResponseEventViewModel).surveyName);
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent: {
				const registrationEvent = item.toJs() as Api.IEventRegistrationSurveyResponseEvent;
				const isAttending = registrationEvent.response?.isAttending;
				const surveyName = (item as Api.SurveyResponseEventViewModel).surveyName;
				if (isAttending == null) {
					return decorateSurveyNameInTitle(item.title, surveyName);
				}
				return (
					<span>
						{contactName} registered as{' '}
						<span className={css(styleSheet.attendancePillWrapper)}>
							<Pill
								variant='label'
								color={isAttending ? 'green' : 'red'}
								label={isAttending ? 'Attending' : 'Not Attending'}
							/>
						</span>{' '}
						to the event &ldquo;<strong>{surveyName}</strong>&rdquo;
					</span>
				);
			}
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
				return (item as Api.HandwrittenCardOrderEventViewModel).toJs().title;
			case Api.TimelineEventType.FileRequestSurveyResponseEvent:
				return item.toJs().title;
			case Api.TimelineEventType.FileDownloadEvent:
				return item.title;
			default:
				return 'Unknown event';
		}
	};

	const getSubtitle = () => {
		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
				return (item as Api.SentEmailEventViewModel).note?.preview;
			case Api.TimelineEventType.HtmlNewsletterEvent:
				// eslint-disable-next-line no-case-declarations
				const newsletterSummary = (item as Api.HtmlNewsletterEventViewModel).toJs().summary;
				if (newsletterSummary) {
					return `Subject: ${newsletterSummary}`;
				}
				return undefined;
			case Api.TimelineEventType.ExternalSentEmailEvent:
				// eslint-disable-next-line no-case-declarations
				const externalEmailSummary = (item as Api.ExternalSentEmailEventViewModel).toJs().summary;
				if (externalEmailSummary) {
					return `Subject: ${externalEmailSummary}`;
				}
				return undefined;
			case Api.TimelineEventType.MeetingEvent:
			case Api.TimelineEventType.MeetingScheduledEvent:
				return `Meeting name: ${(item as Api.MeetingEventViewModel).toJs().summary}`;
			case Api.TimelineEventType.DealCreatedEvent:
			case Api.TimelineEventType.DealUpdatedEvent:
				return 'Deal created';
			case Api.TimelineEventType.NoteEvent:
			case Api.TimelineEventType.SkipLeadEvent:
				return (item as Api.NoteEventViewModel).note.preview;
			case Api.TimelineEventType.ActionItemEvent:
				return <ActionItemSubtitle item={item as Api.ActionItemEventViewModel} />;
			case Api.TimelineEventType.ConversationThreadEvent:
				return (item as Api.ConversationThreadEventViewModel)?.lastMessages?.slice(-1)[0]?.text;
			case Api.TimelineEventType.PhoneCallEvent:
				return (item as Api.PhoneCallEventViewModel)?.phoneCall?.preview;
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent:
				return 'Phone call created';
			case Api.TimelineEventType.FollowUpEvent:
			case Api.TimelineEventType.CancelledFollowUpEvent:
			case Api.TimelineEventType.RescheduledFollowUpEvent:
				return 'Follow up created';
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
				return (item as Api.SatisfactionSurveyResponseEventViewModel).toJs().summary;
			case Api.TimelineEventType.CustomSurveyResponseEvent:
			case Api.TimelineEventType.DonationSurveyResponseEvent:
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent:
				return item.toJs().summary;
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
				return (item as Api.HandwrittenCardOrderEventViewModel).toJs().summary;
			case Api.TimelineEventType.FileRequestSurveyResponseEvent: {
				const files = (item as Api.FileRequestSurveyResponseEventViewModel).response?.files;
				if (!files?.length) return null;
				return (
					<div className={css(styleSheet.fileListContainer)}>
						{files.map(file => (
							<div key={file.id} className={css(styleSheet.fileListItem)}>
								<span className={css(styleSheet.fileListItemLight)}>•</span>
								<span>{file.fileName}</span>
								{file.fileSize != null && (
									<span className={css(styleSheet.fileListItemLight)}>({getFileSizeStringValue(file.fileSize)})</span>
								)}
							</div>
						))}
					</div>
				);
			}
			case Api.TimelineEventType.FileDownloadEvent: {
				const files = (item as Api.FileDownloadEventViewModel).files;
				if (!files?.length) return null;
				return (
					<div className={css(styleSheet.fileListContainer)}>
						{files?.map(file => (
							<div key={file.id} className={css(styleSheet.fileListItem)}>
								<span className={css(styleSheet.fileListItemLight)}>•</span>
								<span>{file.fileName}</span>
								{file.fileSize != null && (
									<span className={css(styleSheet.fileListItemLight)}>({getFileSizeStringValue(file.fileSize)})</span>
								)}
							</div>
						))}
					</div>
				);
			}
			default:
				return 'Unknown event';
		}
	};

	const getIcon = () => {
		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
			case Api.TimelineEventType.ExternalSentEmailEvent:
				return <EmailIcon fillColor={THEME.colors.primary.main} />;
			case Api.TimelineEventType.HtmlNewsletterEvent:
				return <EmailIcon fillColor={THEME.colors.secondary.main} />;
			case Api.TimelineEventType.MeetingEvent:
			case Api.TimelineEventType.MeetingScheduledEvent:
				return <CalendarDateIcon fillColor={THEME.colors.secondary.main} />;
			case Api.TimelineEventType.DealCreatedEvent:
			case Api.TimelineEventType.DealUpdatedEvent:
				return <DollarSignIcon style={{ color: THEME.colors.secondary.main }} />;
			case Api.TimelineEventType.NoteEvent:
			case Api.TimelineEventType.SkipLeadEvent:
				return <NotesIcon fillColor={noteTintColor} />;
			case Api.TimelineEventType.ActionItemEvent: {
				const actionItemEvent = item as Api.ActionItemEventViewModel;
				return <ActionItemCheckboxIcon actionItem={actionItemEvent.viewmodel} />;
			}
			case Api.TimelineEventType.ConversationThreadEvent:
				return <TextingIcon fill={warningDark} />;
			case Api.TimelineEventType.PhoneCallEvent:
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent:
				return <CallIcon fillColor={THEME.colors.success.main} />;
			case Api.TimelineEventType.FollowUpEvent:
			case Api.TimelineEventType.CancelledFollowUpEvent:
			case Api.TimelineEventType.RescheduledFollowUpEvent:
				return <ClockIcon fillColor={THEME.colors.secondary.main} />;
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
				return <SatisfactionSurveyIcon fillColor='#F3CE0D' />;
			case Api.TimelineEventType.CustomSurveyResponseEvent:
				return <CustomSurveyIcon fillColor={THEME.colors.primary.main} />;
			case Api.TimelineEventType.DonationSurveyResponseEvent:
				return <DonationSurveyIcon fillColor='#E86C8D' />;
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent:
				return <EventsSurveyIcon fillColor={THEME.colors.success.main} />;
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
				return <PostageIcon fillColor={THEME.colors.primary.main} />;
			case Api.TimelineEventType.FileRequestSurveyResponseEvent:
				return <UploadIconSimple style={{ color: THEME.colors.primary.main }} />;
			case Api.TimelineEventType.FileDownloadEvent:
				return <DownloadIconV2 color={THEME.colors.primary.main} />;
			default:
				return null;
		}
	};

	const getBadge = () => {
		const badges = [];
		if (eventType === Api.TimelineEventType.SentEmailEvent || eventType === Api.TimelineEventType.HtmlNewsletterEvent) {
			const emailEvent = item as Api.SentEmailEventViewModel;
			const openDate = emailEvent.openDate;
			const status = (emailEvent.note?.context as Api.IEmailMessageSendContext)?.status;

			badges.push(
				openDate ? (
					<>
						<div
							className={css(styleSheet.emailBadgeContainer)}
							style={{ color: header }}
							key={`timeline-card-${item.id}-email-badge-viewed`}
						>
							<span className={css(styleSheet.emailBadgeCheckmarkIcon)} style={{ background: emailActivityTintColor }}>
								<CheckmarkIcon fillColor={THEME.colors.background.main} />
							</span>
							Viewed on {moment(openDate).format('MM/DD/YYYY')}
						</div>
						{status === Api.EmailTransactionStatus.MachineOpened ? (
							<Tooltip>
								<TooltipTrigger className={css(styleSheet.hoverCardTrigger)}>
									<QuestionMarkIcon fillColor={brandPrimary} />
								</TooltipTrigger>
								<TooltipContent side='bottom' align='center'>
									This email was opened by an Apple device, we can&rsquo;t determine if this contact has viewed the
									email.
								</TooltipContent>
							</Tooltip>
						) : null}
					</>
				) : (
					<div
						className={css(styleSheet.emailBadgeContainer)}
						style={{ color: header }}
						key={`timeline-card-${item.id}-email-badge-not-viewed`}
					>
						Email not viewed
					</div>
				)
			);

			if (!(item as Api.SentEmailEventViewModel)?.viewmodel?.rawContentState) {
				badges.push(
					<div
						className={css(styleSheet.emailBadgeContainer)}
						style={{ color: header }}
						key={`timeline-card-${item.id}-email-badge-sent-outside-of-levitate`}
					>
						Sent outside of Levitate
					</div>
				);
			}
		}

		return badges.length > 0 ? <div className={css(styleSheet.emailBadges)}>{badges}</div> : null;
	};

	const getAction = () => {
		if (eventType === Api.TimelineEventType.SentEmailEvent || eventType === Api.TimelineEventType.HtmlNewsletterEvent) {
			if (!(item as Api.SentEmailEventViewModel)?.viewmodel?.rawContentState) {
				return null;
			}
		}

		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
			case Api.TimelineEventType.HtmlNewsletterEvent:
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
				return (
					<Button size='icon' variant='link' onClick={() => setIsOpen(!isOpen)}>
						<ChevronDown className={css(styleSheet.chevronDown, isOpen && styleSheet.chevronDownOpen)} />
					</Button>
				);
			case Api.TimelineEventType.NoteEvent:
			case Api.TimelineEventType.SkipLeadEvent:
				return (
					<div className={css(styleSheet.actionMenuContainer)}>
						<Button size='icon' variant='link' onClick={() => setIsOpen(!isOpen)}>
							<ChevronDown className={css(styleSheet.chevronDown, isOpen && styleSheet.chevronDownOpen)} />
						</Button>
						{renderMenu(item)}
					</div>
				);
			case Api.TimelineEventType.ActionItemEvent:
				return (
					<div className={css(styleSheet.actionMenuContainer)}>
						<ActionItemAction item={item as Api.ActionItemEventViewModel} />
						<Button size='icon' variant='link' onClick={() => setIsOpen(!isOpen)}>
							<ChevronDown className={css(styleSheet.chevronDown, isOpen && styleSheet.chevronDownOpen)} />
						</Button>
						{renderMenu(item)}
					</div>
				);
			case Api.TimelineEventType.PhoneCallEvent:
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent: {
				const phoneCallNote = item as Api.NoteEventViewModel;
				if (phoneCallNote.viewmodel.rawContentState) {
					return (
						<div className={css(styleSheet.actionMenuContainer)}>
							<Button size='icon' variant='link' onClick={() => setIsOpen(!isOpen)}>
								<ChevronDown className={css(styleSheet.chevronDown, isOpen && styleSheet.chevronDownOpen)} />
							</Button>
							{renderMenu(item)}
						</div>
					);
				}
				return null;
			}
			case Api.TimelineEventType.ConversationThreadEvent:
				return (
					<Button variant='link' onClick={() => setIsTextingModalOpen(true)} styles={[styleSheet.seeMoreButton]}>
						<span className={css(styleSheet.seeMoreButtonIcon)}>
							<EyeIcon />
						</span>
						<span className={css(styleSheet.seeMoreButtonText)}>See More</span>
					</Button>
				);
			case Api.TimelineEventType.CustomSurveyResponseEvent:
			case Api.TimelineEventType.DonationSurveyResponseEvent:
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent:
				return (
					<Button size='icon' variant='link' onClick={() => setIsOpen(!isOpen)}>
						<ChevronDown className={css(styleSheet.chevronDown, isOpen && styleSheet.chevronDownOpen)} />
					</Button>
				);
			case Api.TimelineEventType.ExternalSentEmailEvent:
			case Api.TimelineEventType.MeetingEvent:
			case Api.TimelineEventType.MeetingScheduledEvent:
			case Api.TimelineEventType.DealCreatedEvent:
			case Api.TimelineEventType.DealUpdatedEvent:
			case Api.TimelineEventType.FollowUpEvent:
			case Api.TimelineEventType.CancelledFollowUpEvent:
			case Api.TimelineEventType.RescheduledFollowUpEvent:
			default:
				return null;
		}
	};

	const getExtraContent = () => {
		switch (eventType) {
			case Api.TimelineEventType.SentEmailEvent:
			case Api.TimelineEventType.ReceivedEmailEvent:
			case Api.TimelineEventType.ViewedEmailEvent:
			case Api.TimelineEventType.RepliedEmailEvent:
			case Api.TimelineEventType.HtmlNewsletterEvent:
				return (
					<>
						<RichContentDocumentEditor
							autoFocus={false}
							className='note-editor-body-editor'
							config={DefaultCompactEditorConfig}
							contentState={convertRawRichTextContentStateToRichContentEditorState(
								(item as Api.SentEmailEventViewModel).note?.rawContentState
							)}
							readOnly={true}
						/>

						{(item as Api.SentEmailEventViewModel).note?.attachments && (
							<Attachments attachments={(item as Api.SentEmailEventViewModel).note?.attachments} />
						)}
					</>
				);
			case Api.TimelineEventType.NoteEvent:
			case Api.TimelineEventType.SkipLeadEvent:
				return (
					<>
						<RichContentDocumentEditor
							autoFocus={false}
							className='note-editor-body-editor'
							config={DefaultCompactEditorConfig}
							contentState={convertRawRichTextContentStateToRichContentEditorState(
								(item as Api.NoteEventViewModel).viewmodel.rawContentState
							)}
							readOnly={true}
						/>
						{(item as Api.NoteEventViewModel).viewmodel.attachments && (
							<Attachments attachments={(item as Api.NoteEventViewModel).viewmodel.attachments} />
						)}
					</>
				);
			case Api.TimelineEventType.HandwrittenCardOrderEvent:
				return <HandWrittenCardExtraContent item={item} />;
			case Api.TimelineEventType.SatisfactionSurveyResponseEvent:
				return <EntityTimelineSurveyResponseEventContent event={item as Api.SurveyResponseEventViewModel} />;
			case Api.TimelineEventType.PhoneCallEvent:
			case Api.TimelineEventType.PhoneCallCompletedEvent:
			case Api.TimelineEventType.UntrackedPhoneCallEvent: {
				const phoneCallNote = item as Api.NoteEventViewModel;
				if (phoneCallNote.viewmodel.rawContentState) {
					return (
						<RichContentDocumentEditor
							autoFocus={false}
							className='note-editor-body-editor'
							config={DefaultCompactEditorConfig}
							contentState={convertRawRichTextContentStateToRichContentEditorState(
								phoneCallNote.viewmodel.rawContentState
							)}
							readOnly={true}
						/>
					);
				}
				return null;
			}
			case Api.TimelineEventType.ActionItemEvent:
				return (
					<RichContentDocumentEditor
						autoFocus={false}
						className='note-editor-body-editor'
						config={DefaultCompactEditorConfig}
						contentState={convertRawRichTextContentStateToRichContentEditorState(
							(item as Api.ActionItemEventViewModel).toJs().actionItem.content
						)}
						readOnly={true}
					/>
				);
			case Api.TimelineEventType.CustomSurveyResponseEvent:
			case Api.TimelineEventType.DonationSurveyResponseEvent:
			case Api.TimelineEventType.EventRegistrationSurveyResponseEvent:
				return <EntityTimelineSurveyResponseEventContent event={item as Api.SurveyResponseEventViewModel} />;
			case Api.TimelineEventType.ExternalSentEmailEvent:
			case Api.TimelineEventType.MeetingEvent:
			case Api.TimelineEventType.MeetingScheduledEvent:
			case Api.TimelineEventType.DealCreatedEvent:
			case Api.TimelineEventType.DealUpdatedEvent:
			case Api.TimelineEventType.ConversationThreadEvent:
			case Api.TimelineEventType.FollowUpEvent:
			case Api.TimelineEventType.CancelledFollowUpEvent:
			case Api.TimelineEventType.RescheduledFollowUpEvent:
			default:
				return null;
		}
	};

	return (
		<div className={css(styleSheet.timelineItemContainer)}>
			<div className={css(styleSheet.timelineItemTime)}>
				<time
					dateTime={moment(item.timestamp).format('YYYY-MM-DDThh:mm A')}
					className={css(styleSheet.timelineItemTime)}
				>
					{moment(item.timestamp).format('h:mm A')}
				</time>
			</div>
			<div className={css(styleSheet.timelineItemLine)}>
				<div className={css(styleSheet.timelineItemLineIcon)} />
			</div>
			<div className={css(styleSheet.timelineItemContent)}>
				<div
					className={css(
						styleSheet.timelineItemCard,
						isActionItem ? (isOverdue ? styleSheet.timelineItemActionItemOverdue : null) : null
					)}
				>
					<div className={css(styleSheet.timelineItemCardMainContent)}>
						<div className={css(styleSheet.timelineItemCardIcon)}>{getIcon()}</div>
						<div className={css(styleSheet.timelineItemCardContent)}>
							<div className={css(styleSheet.timelineItemCardContentTitle)}>
								<span className={css(styleSheet.timelineItemCardContentTitleText)}>{getTitle()}</span>
							</div>
							<div className={css(styleSheet.timelineItemCardContentSubtitle)}>
								<span className={css(styleSheet.timelineItemCardContentSubtitleText)}>{getSubtitle()}</span>
							</div>
							{getBadge()}
						</div>
						<div className={css(styleSheet.timelineItemCardAction)}>{getAction()}</div>
					</div>

					<div
						className={css(
							styleSheet.timelineItemExtraContentContainer,
							isOpen && styleSheet.timelineItemExtraContentContainerOpen
						)}
					>
						<Separator />

						<div className={css(styleSheet.timelineItemExpandedContent)}>
							<span className={css(styleSheet.timelineItemCardContentSubtitleText)}>{getExtraContent()}</span>
						</div>
					</div>
				</div>
			</div>

			{eventType === Api.TimelineEventType.ConversationThreadEvent && (
				<TextingCampaignRegistrationContextProvider>
					<ConversationThreadModal
						isOpen={isTextingModalOpen}
						onRequestClose={() => setIsTextingModalOpen(false)}
						conversationEvent={item as Api.ConversationThreadEventViewModel}
						entity={item}
					/>
				</TextingCampaignRegistrationContextProvider>
			)}

			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!showDeletingRichContentModal,
					onRequestClose: onDeleteContentConfirmationDialogRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title={`Are you sure you want to delete this ${
					showDeletingRichContentModal?.toJs()?._type === 'ActionItem' ? 'action item' : 'note'
				}?`}
			/>
		</div>
	);
};
